import {Directive, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {LocalizationService}                                from '@progress/kendo-angular-l10n';
import {nameOf}                                             from '../../ap-core/utils/ap-name-of';
import {ApTranslationService}                               from '../../ap-utils/service/ap-translation.service';
import {FileValidationError}                                from '../types/ap-file-upload.types';
import {Messages}                                           from '@progress/kendo-angular-dialog';

/**
 * Directive for handling file upload validation messages and translations.
 * Extends the base Messages class to provide custom file upload related messages.
 */
@Directive({
  selector: '[fileUploadMessages]',
  exportAs: 'fileUploadMessages',
  providers: [
    {
      provide: Messages,
      useExisting: ApFileUploadMessagesDirective
    }
  ]
})
export class ApFileUploadMessagesDirective extends Messages implements OnInit, OnChanges {
  /** Custom message for non-unique file validation error */
  @Input() public fileIsNotUnique: string;

  constructor(protected localizationService: LocalizationService,
              private translationService: ApTranslationService) {
    super();
  }

  /**
   * Initializes the directive by registering the default file uniqueness message
   * @public
   */
  public ngOnInit(): void {
    this._registerFileIsNotUniqueMessage();
  }

  /**
   * Handles changes to input properties by re-registering messages when fileIsNotUnique changes
   * @param changes SimpleChanges object containing changed properties
   * @public
   */
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes[nameOf<ApFileUploadMessagesDirective>('fileIsNotUnique')]) {
      this._registerFileIsNotUniqueMessage();
    }
  }

  /**
   * Registers multiple validation messages with translation support
   * @param messagesToRegister Array of FileValidationError objects to register
   * @public
   */
  public registerMessage(messagesToRegister: FileValidationError[]): void {
    if (!!this.localizationService && !!messagesToRegister && messagesToRegister.length > 0) {
      messagesToRegister.forEach((fileValidationError: FileValidationError) => {
        const translatedValue = this.translationService.translate(fileValidationError.ErrorMessage);
        this.localizationService.register(fileValidationError.ErrorKey, translatedValue, true);
      });
    }
  }

  /**
   * Registers the file uniqueness validation message with either custom or default text
   * @private
   */
  private _registerFileIsNotUniqueMessage(): void {
    const messagesToRegister: FileValidationError = {
      ErrorKey: nameOf<ApFileUploadMessagesDirective>('fileIsNotUnique'),
      ErrorMessage: this.fileIsNotUnique || 'Same file is already in use'
    };
    this.registerMessage([messagesToRegister]);
  }
}
